import { useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Search, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

export const FleetImportDialog = ({ open, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    try {
      const { data } = await api.get("/fleet/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "modello_disponibilita_mezzi.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const send = async (confirm) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("confirm", confirm);
    setBusy(true);
    try {
      const { data } = await api.post("/fleet/import", fd);
      if (confirm) { toast.success(`Importati ${data.vehicles} mezzi e ${data.workshops} officine`); setFile(null); setPreview(null); onDone(); }
      else setPreview(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="fleet-import-dialog">
        <DialogHeader>
          <DialogTitle>Caricamento massivo mezzi</DialogTitle>
          <DialogDescription>Scarica il modello, compilalo e caricalo. I mezzi già presenti (stessa targa) vengono aggiornati. Colonna DEFINITIVO = SI per trasferire il mezzo in modo definitivo alla sede indicata in TRASFERITO A.</DialogDescription>
        </DialogHeader>
        <Button variant="outline" onClick={download} data-testid="fleet-template-button"><Download className="mr-2 h-4 w-4" /> Scarica modello Excel</Button>
        <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm text-slate-600 hover:border-slate-400">
          <FileSpreadsheet className="h-4 w-4 text-emerald-600" /><span className="truncate">{file ? file.name : "Scegli file compilato..."}</span>
          <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="fleet-import-file-input" />
        </label>
        <Button onClick={() => send(false)} disabled={!file || busy} data-testid="fleet-import-preview-button"><Search className="mr-2 h-4 w-4" /> Analizza file</Button>
        {preview && (
          <div className="space-y-3 rounded-lg border p-4 text-sm" data-testid="fleet-import-preview">
            <p><b data-testid="fleet-import-count">{preview.vehicles}</b> mezzi validi ({preview.new} nuovi, {preview.update} da aggiornare) · {preview.workshops} officine{preview.definitive > 0 && ` · ${preview.definitive} trasferimenti definitivi`}</p>
            <p className="text-xs text-slate-500">{preview.per_station.map((s) => `${s.station_name}: ${s.count}`).join(" · ")}</p>
            {preview.errors.length > 0 && (
              <div className="max-h-40 overflow-auto rounded-md bg-rose-50 p-2 text-xs text-rose-700" data-testid="fleet-import-errors">
                <p className="mb-1 font-semibold">{preview.errors.length} righe scartate:</p>
                {preview.errors.map((e) => <p key={`${e.sheet}-${e.row}`}>Riga {e.row} ({e.plate}): {e.error}</p>)}
              </div>
            )}
            <Button onClick={() => send(true)} disabled={busy || (!preview.vehicles && !preview.workshops)} data-testid="fleet-import-confirm-button"><Upload className="mr-2 h-4 w-4" /> Importa</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
